import { camelizeKeys, decamelizeKeys } from "humps";
import config from "../config";
import { getReqCookies, setCookies } from "../utils/cookies";
import videos from "./videos";
import user from "./user";
import contentTags from "./contentTags";
import adProducts from "./adProducts";
import printables from "./printables";
import events from "./events";
import activitySession from "./activitySession";
import search from "./search";
import spotlight from "./spotlight";
import shelves from "./shelves";

/**
 * Check the status code of a response. Returns response if status is ok.
 * Throws error otherwise.
 * @param {Object} response - Fetch response
 * @returns {Promise, Error} Fetch response
 */
const checkStatus = (response) => {
  if (response.ok) {
    return response;
  }
  const error = new Error(response.statusText);
  error.code = response.status;
  error.response = response;
  throw error;
};

/**
 * Create function to fire an API request using fetch.
 * @param {Object} ctx - Context object
 * @param {Object} ctx.req - Request object
 * @param {Object} ctx.res - Response object
 * @returns {(method: String, uri: String, options: Object) => Promise}
 */
const request = ({ req, res } = {}) => (method, uri, options = {}) => {
  const {
    headers: defaultHeaders,
    includeClientToken,
    body,
    ...rest
  } = options;
  const API_BASEURL =
    process.env.API_BASEURL || process.env.NEXT_PUBLIC_API_BASEURL;

  const endpoint = `${API_BASEURL}/${uri}`;

  // Assigns the appropriate headers
  let headers = {
    ...defaultHeaders,
    "GN-Device-Platform": "Universe Web",
    "GN-Device-Version": config.APP_VERSION,
  };

  if (includeClientToken) {
    headers = {
      ...headers,
      Authorization: `bearer ${config.UNIVERSE_WEB_SECRET_KEY}`,
    };
  }

  if (req) {
    headers = {
      ...headers,
      cookie: getReqCookies(req),
    };
  }

  if (req && req.headers["user-agent"]) {
    headers = {
      ...headers,
      "User-Agent": req.headers["user-agent"],
    };
  }

  // forward cloud trace fields
  if (req && req.headers["x-cloud-trace-context"]) {
    headers = {
      ...headers,
      "X-Cloud-Trace-Context": req.headers["x-cloud-trace-context"],
    };
  }

  // for the nextjs backend calls to the api this will forward client ip
  // this helps with the rack-attack api calls
  if (req && req.headers["x-forwarded-for"]) {
    headers = {
      ...headers,
      "X-Forwarded-For": req.headers["x-forwarded-for"],
    };
  }

  headers = {
    ...headers,
    "content-type": "application/json",
  };

  return fetch(endpoint, {
    credentials: "include",
    method,
    headers,
    body: JSON.stringify(decamelizeKeys(body)),
    ...rest,
  }).then(setCookies({ req, res }));
};

/**
 * Convert response to JSON.
 * @param {Object} response - Fetch response object.
 * @returns {Object} Converted body to JSON.
 */
const toJSON = async (response) => {
  // A 204 response is terminated by the first empty line after the header fields because it cannot contain a message body. So we can not call response.json().
  const json = response.status !== 204 ? await response.json() : null;
  return camelizeKeys(json);
};

const validateJSON = (schema) => async (json) => {
  await schema.validateAsync(json);
  return json;
};

/**
 * A utility for interfacing with the API.
 * @param {Object} ctx - Context object.
 * @returns {Object} Object of methods for API
 */
export default (ctx) => {
  const requestWithCookie = request(ctx);
  const restfulMethods = {
    del: (uri, options) => requestWithCookie("DELETE", uri, options),
    get: (uri, options) => requestWithCookie("GET", uri, options),
    patch: (uri, options) => requestWithCookie("PATCH", uri, options),
    post: (uri, options) => requestWithCookie("POST", uri, options),
    put: (uri, options) => requestWithCookie("PUT", uri, options),
  };
  const helpers = {
    checkStatus,
    toJSON,
    validateJSON,
  };

  return {
    ...videos({ ...restfulMethods, ...helpers }),
    ...user({ ...restfulMethods, ...helpers }),
    ...contentTags({ ...restfulMethods, ...helpers }),
    ...adProducts({ ...restfulMethods, ...helpers }),
    ...printables({ ...restfulMethods, ...helpers }),
    ...events({ ...restfulMethods, ...helpers }),
    ...activitySession({ ...restfulMethods, ...helpers }),
    ...search({ ...restfulMethods, ...helpers }),
    ...spotlight({ ...restfulMethods, ...helpers }),
    ...shelves({ ...restfulMethods, ...helpers }),
  };
};
